'use client';

import {useEffect, useState} from 'react';
import {datadogRum} from '@datadog/browser-rum';

import {historyStorage} from '../../../stores/HistoryContext/context';

import {seedHistory} from './utils';

interface UseNavigationHistoryParams {
  asPath: string;
  hostedSource: string | null;
  route: string;
}

/**
 * Tracks the paths the user has visited, and mirrors them to storage and to
 * Datadog.
 *
 * The history lives in state rather than being recomputed from storage on every
 * render. `historyStorage.get()` hands back a fresh array each call, so reading
 * it during render gave the value a new identity every time — which is why this
 * used to sit behind a `useMemo` whose dependency list was the only thing
 * pinning the writes to route changes. Side effects in a memo are a
 * Rules-of-React violation regardless of what any lint rule says about it.
 *
 * Storage is read once, to seed. After that the route change advances the list
 * and the effect is what persists it and opens the Datadog view.
 */
const useNavigationHistory = ({
  asPath,
  hostedSource,
  route,
}: UseNavigationHistoryParams): string[] => {
  const [history, setHistory] = useState<string[]>(() =>
    seedHistory(historyStorage.get(), hostedSource, asPath),
  );
  const [lastPath, setLastPath] = useState(asPath);

  // Advancing during render rather than in an effect: the new path is derived
  // from a prop change, so consumers should never see the stale list.
  if (asPath !== lastPath) {
    setLastPath(asPath);
    setHistory((previous) =>
      previous[previous.length - 1] === asPath
        ? previous
        : [...previous, asPath],
    );
  }

  // effect:audited — mirrors the in-memory history to storage and tells Datadog
  // a new view opened. Both are external systems, which is what an effect is for.
  useEffect(() => {
    historyStorage.set(history);
    datadogRum.startView(route);
  }, [history, route]);

  return history;
};

export default useNavigationHistory;
